/**
 * WCC360 — salary band plausibility.
 *
 * Salary reaches the app from two places that never met: numeric LPA figures
 * used for scoring (`career_market_intelligence`, else the bundle fallback),
 * and free-text bands in the Career Intelligence shards, copied straight from
 * a spreadsheet. Neither was ever checked against the role's own seniority, so
 * a Senior/Director row could ship a band that reads like a first internship.
 *
 * We do not invent or "correct" a number here — that would be worse. An
 * implausible band is dropped, which the rest of the app already handles
 * honestly: a career with no salary is shown without one, is scored neutral on
 * pay, and can never appear as a Best match on the strength of a number we
 * don't trust.
 */

export type LevelTier = "entry" | "mid" | "senior" | "executive" | "unknown";

/** The role's seniority, from the registry's free-text `level`. */
export function careerLevelTier(level: string | null | undefined): LevelTier {
  const s = (level ?? "").toLowerCase();
  if (!s) return "unknown";
  if (/(chief|c-suite|cxo|executive|president|provost|dean|partner|board)/.test(s)) return "executive";
  if (/(senior|lead|principal|head|director|manager|consultant|specialist)/.test(s)) return "senior";
  if (/(mid|intermediate|experienced|associate)/.test(s)) return "mid";
  if (/(entry|junior|trainee|graduate|apprentice|intern|assistant|helper|operator)/.test(s)) return "entry";
  return "unknown";
}

/**
 * Plausible annual band per tier, in ₹ lakh. Wide on purpose: this catches
 * data faults (a Dean at ₹1.5 LPA, an intern at ₹400 LPA), not unusual pay.
 */
const TIER_RANGE: Record<LevelTier, { min: number; max: number }> = {
  entry: { min: 0.6, max: 80 },
  mid: { min: 1.5, max: 150 },
  senior: { min: 4, max: 400 },
  executive: { min: 8, max: 900 },
  unknown: { min: 0.6, max: 900 },
};

export interface SanitizedBand {
  lo: number | null;
  hi: number | null;
  /** Why the band was dropped, for logs and provenance. Null when kept. */
  rejected: string | null;
}

/**
 * Keep a numeric band only when it is internally coherent AND plausible for
 * the seniority of the role. Anything else comes back null.
 */
export function sanitizeSalaryBand(
  lo: number | null | undefined,
  hi: number | null | undefined,
  level: string | null | undefined,
): SanitizedBand {
  const drop = (why: string): SanitizedBand => ({ lo: null, hi: null, rejected: why });
  if (lo == null && hi == null) return { lo: null, hi: null, rejected: null };
  const l = typeof lo === "number" && Number.isFinite(lo) ? lo : null;
  const h = typeof hi === "number" && Number.isFinite(hi) ? hi : null;
  // Zero is a salary; absence is not. An unpriced row must carry null, never 0.
  if (h == null || h <= 0) return drop("no upper bound");
  if (l != null && l > h) return drop("lower bound above upper bound");
  const { min, max } = TIER_RANGE[careerLevelTier(level)];
  if (h > max) return drop(`upper bound above what this level pays (${h} > ${max} LPA)`);
  if (h < min) return drop(`upper bound below what this level pays (${h} < ${min} LPA)`);
  if (l != null && l < min * 0.5) return drop(`lower bound implausible for this level (${l} LPA)`);
  return { lo: l, hi: h, rejected: null };
}

/** "₹6 LPA – ₹15 LPA", "6-15 LPA", "₹12,00,000" → numeric lakh bounds. */
export function parseLpaRange(text: string | null | undefined): { lo: number | null; hi: number | null } {
  if (!text) return { lo: null, hi: null };
  const t = text.replace(/,/g, "");
  // Units are read per number, not once for the whole string: a band may mix
  // them ("₹50 LPA – ₹1.4 Cr"), and scaling both by crore inflates the low end.
  const matches = [...t.matchAll(/(\d+(?:\.\d+)?)\s*([A-Za-z]*)/g)];
  if (matches.length === 0) return { lo: null, hi: null };
  const anyCrore = /crore|cr\b/i.test(t);
  const anyLakh = /lpa|lakh|lac\b/i.test(t);
  const scaled = matches.map((m) => {
    const n = Number(m[1]);
    const unit = (m[2] ?? "").toLowerCase();
    if (/^(crore|cr)$/.test(unit)) return n * 100;
    if (/^(lpa|lakh|lakhs|lac|lacs|l)$/.test(unit)) return n;
    // No unit on this figure: inherit only when the string is unambiguous.
    if (anyCrore && !anyLakh) return n * 100;
    // A raw rupee figure (₹12,00,000) rather than a lakh figure.
    return n >= 10_000 ? n / 100_000 : n;
  });
  const lo = Math.min(...scaled);
  const hi = Math.max(...scaled);
  return { lo, hi: hi === lo ? hi : hi };
}

/**
 * Display band for a role, or null when the figures don't hold up against its
 * seniority. Used for the free-text Career Intelligence bands.
 */
export function sanitizeSalaryText(
  text: string | null | undefined,
  level: string | null | undefined,
): string | null {
  if (!text) return null;
  if (/varies|not available|n\/?a\b/i.test(text)) return null;
  const { lo, hi } = parseLpaRange(text);
  if (lo == null && hi == null) return text;
  return sanitizeSalaryBand(lo, hi, level).rejected ? null : text;
}
